import React from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

const MemuVsMem0 = () => {
  return (
    <div className="doc-content">
      <div className="quickstart-header">
        <p className="quickstart-category">Compare</p>
        <h1>memU vs mem0</h1>
      </div>

      <p id="overview">memU and mem0 are frameworks designed to give AI long-term memory, aimed at various agent applications, helping overcome the context window limits of LLMs. Depending on your needs, memU can serve as a replacement for mem0. This guide compares memU and mem0 to help you choose the best fit for your project.</p>

      <hr />

      <h2 id="when-to-choose-memu">When to choose memU</h2>

      <h3>1. You need deep search and precise event recall</h3>
      <p>Traditional RAG struggles with deep searches and event-level semantic connections. memU uses a layered storage architecture to gradually convert diverse inputs into queryable, interpretable text memories, organized in interlinked files. These file connections act like semantic hyperlinks, making deep searches possible. Whether you need to recall the exact time, participants, context chains, or even the details and motivations behind an event, memU delivers precise, traceable results.</p>

      <h3>2. You want AI-accessible and human-readable memory</h3>
      <p>mem0 stores memory mostly as vector summaries—great for machines, not so much for people.</p>
      <p>memU keeps memories in structured, human-readable text files—events, roles, causal chains, and changes are all clearly presented. This not only allows agents to leverage memories for answers but also lets teams read and understand the AI’s “thinking,” boosting transparency and control.</p>

      <h3>3. You want self-evolving, proactive AI</h3>
      <p>memU automatically adjusts memory structure based on usage patterns. It reorganizes itself over time according to user behavior, topics, and tasks, dynamically creating, merging, or rewriting files, turning “unordered” memories into structured knowledge. It also actively optimizes based on usage frequency and infers semantic links from past conversations. Over time, the AI becomes more intuitive and like a long-term assistant that grows with you.</p>

      <hr />

      <h2 id="when-to-choose-mem0">When to choose mem0</h2>

      <h3>1. Simple and lightweight architecture</h3>
      <p>mem0 is designed to be minimal and easy to set up. Its straightforward structure makes it quick to integrate, with low overhead and no complex dependencies. This simplicity is ideal for small projects, prototypes, or scenarios where you just need basic long-term memory without the complexity of a full-scale memory system.</p>

      <h3>2. Covering RAG’s stateless limitations</h3>
      <p>RAG is great for retrieving facts, but it doesn’t know who the user is, what they asked before, or why something failed last time. mem0 provides continuity, storing preferences, decisions, and contex——not just raw knowledge.</p>

      <hr />

      <h2 id="memu-advantages">memU Advantages</h2>

      <h3>1. File-based: an engineered, auditable, maintainable memory system</h3>
      <ul>
        <li>Each memory is a separate file, traceable and reproducible</li>
        <li>Clear hierarchy: topic → event details</li>
        <li>No reliance on black-box labeling or explicit modeling</li>
        <li>Perfect for large projects, multi-user collaboration, long-running AI agents</li>
      </ul>
      <p><strong>Result:</strong> You can <em>see</em> your memory—and trust it.</p>

      <h3>2. Automatically builds topic structures with cross-topic connections</h3>
      <ul>
        <li>Extracts key info from daily conversations</li>
        <li>Generates files for different topics</li>
        <li>Infers semantic links across topics</li>
        <li>Helps AI actually understand relationships between topics</li>
      </ul>
      <p><strong>Result:</strong> Ideal for complex, multi-tasking, long-term use cases.</p>

      <h3>3. Precise event logging: time, details, context at a glance</h3>
      <ul>
        <li>Exact timestamps</li>
        <li>Full event descriptions</li>
        <li>Clear context sources</li>
        <li>Related objects and semantic links</li>
      </ul>
      <p><strong>Result:</strong> Easier to review, debug, and rely on memory as a real data asset.</p>

      <h3>4. Self-evolving memory agent: memory gets “smarter over time”</h3>
      <ul>
        <li>Adjusts structure based on usage patterns</li>
        <li>Keeps memory clean and accurate</li>
        <li>Becomes more stable and structured with use</li>
      </ul>
      <p><strong>Result:</strong> Major advantage when building long-term AI assistants.</p>

      <h3>5. Built for real products, not just demos</h3>
      <ul>
        <li><strong>Observable</strong>—files are readable</li>
        <li><strong>Sustainable</strong>—memory evolves over time</li>
        <li><strong>Integratable</strong>—works with different LLMs and storage setups</li>
      </ul>
      <p><strong>Result:</strong> Helps you avoid common pitfalls when building AI for real users.</p>

      <hr />

      <h2 id="feature-comparison">Feature Comparison</h2>
      <p>(Coming soon)</p>

      <hr />

      <h2 id="pricing">Pricing</h2>
      <p>memU offers Memory API, Response API, and enterprise customization. Check out full pricing at <a href="https://memu.pro/pricing" target="_blank" rel="noopener noreferrer">https://memu.pro/pricing</a>, or contact us to find the plan that fits your business.</p>

      <hr />

      <h2 id="open-source">Open Source</h2>
      <p>To make integration easy for developers, memU has several open-source repos:</p>
      <ul>
        <li>Core algorithm library: memU</li>
        <li>Memory data server: memU-server</li>
        <li>Visual front-end: memU-ui</li>
      </ul>

      <hr />

      <h2 id="get-started">Get Started</h2>
      <p><strong>Install memU: https://github.com/NevaMind-AI/memU</strong></p>
      <div className="code-block">
        <SyntaxHighlighter
          language="bash"
          style={vscDarkPlus}
          customStyle={{
            margin: 0,
            padding: 0,
            fontSize: '14px',
            lineHeight: '1.5'
          }}
        >{`pip install memu-py`}</SyntaxHighlighter>
      </div>

      <p><strong>Quick Example：</strong></p>
      <div className="info-banner">
        <i className="fa-solid fa-info-circle"></i>
        <div>
          <p>⚠️ Important: Ensure you have Python 3.14+<br/>⚠️ Important: Replace "your-openai-api-key" with your actual OpenAI API key to use the service.</p>
        </div>
      </div>
      <div className="code-block">
        <SyntaxHighlighter
          language="python"
          style={vscDarkPlus}
          customStyle={{
            margin: 0,
            padding: 0,
            fontSize: '14px',
            lineHeight: '1.5'
          }}
        >{`from memu.app import MemoryService
import os

async def main():
    api_key = "your-openai-api-key"
    file_path = os.path.abspath("path/to/memU/tests/example/example_conversation.json")

    # Initialize service with RAG method
    service_rag = MemoryService(
        llm_config={"api_key": api_key},
        embedding_config={"api_key": api_key},
        retrieve_config={"method": "rag"}
    )

    # Memorize
    memory = await service_rag.memorize(resource_url=file_path, modality="conversation")
    for cat in memory.get('categories', []):
        print(f"  - {cat.get('name')}: {(cat.get('summary') or '')[:80]}...")

    queries = [
        {"role": "user", "content": {"text": "Tell me about preferences"}},
        {"role": "user", "content": {"text": "What are their habits?"}}
    ]

    # RAG-based retrieval
    print("\\n[RETRIEVED - RAG]")
    result_rag = await service_rag.retrieve(queries=queries)
    for item in result_rag.get('items', [])[:3]:
        print(f"  - [{item.get('memory_type')}] {item.get('summary', '')[:100]}...")

    # Initialize service with LLM method (reuse same memory store)
    service_llm = MemoryService(
        llm_config={"api_key": api_key},
        embedding_config={"api_key": api_key},
        retrieve_config={"method": "llm"}
    )
    service_llm.store = service_rag.store  # Reuse memory store

    # LLM-based retrieval
    print("\\n[RETRIEVED - LLM]")
    result_llm = await service_llm.retrieve(queries=queries)
    for item in result_llm.get('items', [])[:3]:
        print(f"  - [{item.get('memory_type')}] {item.get('summary', '')[:100]}...")

if __name__ == "__main__":
    import asyncio
    asyncio.run(main())`}
        </SyntaxHighlighter>
      </div>

      <p>To support customization and different deployment setups, the memU project consists of several open-source components:</p>
      <ul>
        <li><strong>memU</strong> — core memory engine <a href="https://github.com/NevaMind-AI/memU" target="_blank" rel="noopener noreferrer">https://github.com/NevaMind-AI/memU</a></li>
        <li><strong>memU-server</strong> — backend service / API for multi-user and multi-agent scenarios <a href="https://github.com/NevaMind-AI/memU-server" target="_blank" rel="noopener noreferrer">https://github.com/NevaMind-AI/memU-server</a></li>
        <li><strong>memU-ui</strong> — web interface for browsing and managing memory visually <a href="https://github.com/NevaMind-AI/memU-ui" target="_blank" rel="noopener noreferrer">https://github.com/NevaMind-AI/memU-ui</a></li>
      </ul>
    </div>
  );
};

export default MemuVsMem0;

